//@ts-nocheck
import ImageResize from 'quill-image-resize-module-react';
import { FC, useCallback, useMemo, useRef } from 'react';
import {
  Quill,
  default as QuillEditor,
  default as ReactQuill,
  ReactQuillProps,
} from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { cn } from '../../lib/utils';

Quill.register('modules/imageResize', ImageResize);

export interface QuillInputProps extends ReactQuillProps {
  toolbar?: boolean;
}

const FORMATS = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
  'color',
  'clean',
  'alt',
  'height',
  'width',
  'style',
  'size',
  'video',
];

const SMALL_FORMATS = FORMATS.filter((format) =>
  [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'clean',
    'alt',
    'height',
    'width',
    'style',
    'size',
  ].includes(format),
);

export const QuillInput: FC<QuillInputProps> = (props) => {
  const { value, onChange, readOnly, toolbar = true, ...otherProps } = props;

  const quill = useRef<ReactQuill>(null);

  const imageHandler = useCallback(() => {
    // Create an input element of type 'file'
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    // When a file is selected
    input.onchange = () => {
      if (!input.files || !input.files[0]) return;
      const file = input.files[0];
      const reader = new FileReader();

      // Read the selected file as a data URL
      reader.onload = () => {
        const imageUrl = reader.result;
        if (!quill.current) return;
        const quillEditor = quill.current.getEditor();

        // Get the current selection range and insert the image at that index
        const range = quillEditor.getSelection(true);
        quillEditor.insertEmbed(range.index, 'image', imageUrl, 'user');
      };

      reader.readAsDataURL(file);
    };
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, false] }],
          ['bold', 'italic', 'underline', 'blockquote'],
          [{ color: [] as never }],
          [
            { list: 'ordered' },
            { list: 'bullet' },
            { indent: '-1' },
            { indent: '+1' },
          ],
          ['link', 'image', 'video'],
          ['clean'],
        ],
        handlers: {
          image: imageHandler,
        },
      },
      clipboard: {
        matchVisual: true,
      },
      imageResize: {
        parchment: Quill.import('parchment'),
        modules: ['Resize', 'DisplaySize'],
        handleStyles: {
          border: 'none',
        },
      },
    }),
    [imageHandler],
  );

  return (
    <QuillEditor
      {...otherProps}
      ref={quill}
      className={cn(
        'h-[calc(100%-48px)]',
        readOnly &&
          'h-full [&_.ql-container]:!border-none [&_.ql-toolbar]:hidden',
        !toolbar && '[&_.ql-formats]:text-center',
        otherProps.className,
      )}
      formats={toolbar ? FORMATS : SMALL_FORMATS}
      modules={toolbar ? modules : {}}
      readOnly={readOnly}
      theme="snow"
      value={value}
      onChange={onChange}
    />
  );
};
